import React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material';

/**
 * ArticleTitle - Componente atômico para exibir o título de um artigo
 * 
 * Este componente segue o princípio do Atomic Design como um "átomo".
 * Exibe o título em uma única linha com tooltip ao passar o mouse.
 */
const ArticleTitle = ({
  children,
  variant = 'default',
  onClick,
  className = '',
  color,
  ...props
}) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = React.useState(false);
  
  // Base para o tamanho de fonte
  const baseFontSize = theme.typography.fontSize;
  // Multiplicadores por variante
  const fontSizeMap = {
    default: 1,
    small: 0.95,
    large: 1.1,
    list: 0.97,
    detailed: 1
  };
  const fontSize = `${baseFontSize * (fontSizeMap[variant] || 1)}px`;

  // Cor base do título
  const baseColor = color || theme.palette.text.primary;
  // Cor no hover (cor do tema) - só aplica se tiver onClick
  const hoverColor = onClick && isHovered ? theme.palette.primary.main : baseColor;
  
  const styles = {
    title: {
      margin: '0 0 8px',
      fontWeight: 600,
      color: hoverColor,
      cursor: onClick ? 'pointer' : 'default',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      width: '100%',
      boxSizing: 'border-box',
      fontSize: 'inherit',
      lineHeight: 1.4,
      transition: 'color 0.2s ease',
      ...(variant === 'detailed' && { margin: '0 0 8px' })
    }
  };

  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
    }
  };

  // Envolver o título em um Tooltip que mostra o texto completo
  return (
    <h3 
        className={`article-title article-title--${variant} ${className}`}
        style={styles.title}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        {...props}
      >
        {children}
    </h3>
  );
};

ArticleTitle.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf([
    'default', 'small', 'large', 'list', 'detailed'
  ]),
  onClick: PropTypes.func,
  className: PropTypes.string,
  color: PropTypes.string
};

export default ArticleTitle;
